import { useRequest } from 'features/request/useRequest';
import type { BimRemoveWorkOrderPricesToActKc2Mutation } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { useSetIsWorkOrderChangeRequired } from 'pages/Project/BimProject/hooks/useSetIsWorkOrderChangeRequired';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimRemoveWorkOrderPricesToActKC2Gql, bimWorkOrderDeletePricesGql } from '../bimWorkOrderPriceQueries';

export const useBimWorkOrderDeletePrices = ({
    workOrderId,
    workOrderPriceIds,
    resetCheckedRows,
    refreshTable,
    workOrderStatus,
}: {
    workOrderId: number;
    workOrderPriceIds: number[];
    resetCheckedRows: () => void;
    refreshTable: () => void;
    workOrderStatus?: WorkOrderStatusEnum;
}) => {
    const dispatch = useDispatch();

    const isInProgress = workOrderStatus === WorkOrderStatusEnum.InProgress;

    const variables = useMemo(
        () => ({
            workOrderId,
            workOrderPriceIds,
        }),
        [workOrderId, workOrderPriceIds],
    );

    const refreshData = useCallback(() => {
        refreshTable();
        resetCheckedRows();
    }, [refreshTable, resetCheckedRows]);

    const [data, status, err, request, deleteSetData] = useRequest({
        requestId: 'bimWorkOrderDeletePrices',
        withEffect: false,
        query: (isInProgress ? bimRemoveWorkOrderPricesToActKC2Gql : bimWorkOrderDeletePricesGql) as any,
    });

    useSetIsWorkOrderChangeRequired({
        useData: isInProgress
            ? (data as BimRemoveWorkOrderPricesToActKc2Mutation)?.bimRemoveWorkOrderPricesToActKC2?.data
            : undefined,
        status,
    });

    const deleteHandler = useCallback(() => {
        dispatch(request(variables));
    }, [request, dispatch, variables]);

    return {
        deleteData: isInProgress
            ? (data as any)?.bimRemoveWorkOrderPricesToActKC2
            : (data as any)?.bimRemoveWorkOrderPrices,
        deleteStatus: status,
        deleteError: err,
        deleteSetData,
        handleDelete: deleteHandler,
        onSuccess: refreshData,
    };
};
